import React, { useEffect, useState, useCallback } from 'react';

import Header from 'components/Header/Header';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faSearch,
  faEdit,
  faTrash,
  faTimes,
  faClipboard,
} from '@fortawesome/free-solid-svg-icons';
import Styles from './Produtos.module.scss';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Creators } from 'store/ducks/produtos';
import { Creators as UserCreators } from 'store/ducks/users';
import ProductEditor from 'components/ProductEditor/ProductEditor';
import { editorRenderables } from 'shared/constants';
import { O, A, C, T } from 'shared/helpers';
import WithPermission from 'components/Template/WithPermission/WithPermission';
import { useEditableContext } from 'shared/hooks';

const ConfirmationDialog = ({ produto, onClose, onConfirm, open }) => (
  <div className={C(Styles.Modal, Styles.FlexibleHeight, [open, Styles.Open])}>
    <main>
      <header>
        <div>Tem certeza que deseja excluir este produto?</div>
        <div id='confirm_product_removal_close_btn'>
          <FontAwesomeIcon icon={faTimes} onClick={onClose} />
        </div>
      </header>
      <div>
        <button className={Styles.Success} onClick={() => onConfirm(produto)}>
          Sim
        </button>
        <button className={Styles.Danger} onClick={onClose}>
          Cancelar
        </button>
      </div>
    </main>
  </div>
);
ConfirmationDialog.propTypes = {
  produto: PropTypes.any.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

const Table = ({ produtos = [], onView, onEdit, onRemove, users = [] }) => (
  <div className={Styles.Table}>
    <div className={Styles.TableHeader}>
      <div className={Styles.TableRow}>
        <div>ID</div>
        <div>Produto</div>
        <div>Atualizado em</div>
        <div>Ações</div>
      </div>
    </div>
    <div className={Styles.TableBody}>
      {produtos.map((produto) => (
        <div className={Styles.TableRow}>
          <div>{produto.id}</div>
          <div>
            {produto.nome}
            {produto.user_id && users && (
              <sub className={Styles.ProductOwner}>
                Pertence ao Etec:{' '}
                {T(users.find((u) => u.id === produto.user_id)).name}
              </sub>
            )}
          </div>

          <div>
            {produto.updated_at ? moment(produto.updated_at).format('L') : null}
          </div>
          <div className={Styles.Controls}>
            <button onClick={() => onView(produto)}>
              <FontAwesomeIcon icon={faSearch} />
              &nbsp; Visualizar
            </button>
            <WithPermission permission='produtos.update'>
              <button onClick={() => onEdit(produto)}>
                <FontAwesomeIcon icon={faEdit} />
                &nbsp; Editar
              </button>
            </WithPermission>
            <WithPermission permission='produtos.delete'>
              <button onClick={() => onRemove(produto)}>
                <FontAwesomeIcon icon={faTrash} />
                &nbsp; Remover
              </button>
            </WithPermission>
          </div>
        </div>
      ))}
    </div>
  </div>
);
Table.propTypes = {
  produtos: PropTypes.arrayOf(PropTypes.any).isRequired,
  onView: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  users: PropTypes.arrayOf(PropTypes.any).isRequired,
};

const DetailsTable = ({ renderables, produto = {}, open, onClose }) => (
  <div className={C(Styles.Modal, Styles.TableElementos, [open, Styles.Open])}>
    <main className={Styles.FlexibleHeight}>
      <header>
        {produto.nome}
        <div id='product_details_close_btn'>
          <FontAwesomeIcon icon={faTimes} onClick={onClose} />
        </div>
      </header>
      <div className={C(Styles.Table, Styles.ContainingTable)}>
        <div className={Styles.TableHeader}>
          <div className={Styles.TableRow}>
            <div>Elemento</div>
            <div>%</div>
          </div>
        </div>
        <div className={Styles.TableBody}>
          {renderables.map((renderable) => (
            <div className={Styles.TableRow}>
              <div>{renderable}</div>
              <div>{produto[renderable]}</div>
            </div>
          ))}
        </div>
      </div>
    </main>
  </div>
);
DetailsTable.propTypes = {
  renderables: PropTypes.arrayOf(PropTypes.string).isRequired,
  produto: PropTypes.any.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default function Produtos() {
  const dispatch = useDispatch();
  const produtos = useSelector((store) => store.produtos.data);
  const networkLoading = useSelector((store) => store.queue.loading);
  const availableUsers = useSelector((store) => store.users.data);
  useEffect(() => {
    dispatch(Creators.getProdutos());
    dispatch(UserCreators.getUsers());
  }, [dispatch]);

  useEffect(() => {
    // Parou de carregar
    if (!networkLoading) {
      dispatch(Creators.getProdutos());
    }
  }, [networkLoading, dispatch]);

  const [form, setForm] = useState({});

  const [selectedProduto, setSelectedProduto] = useState(undefined);

  function createProduto() {
    setForm({
      nome: '',
      classificacao: '',
      ...O(editorRenderables, A('', editorRenderables.length)),
    });
    setSelectedProduto({});
    setEditMode(true);
  }

  const [deleteMode, setDeleteMode] = useState(false);
  function deleteProduto(p) {
    setSelectedProduto(p);
    setDeleteMode(true);
  }

  const [editMode, setEditMode] = useState(false);
  function editProduto(p) {
    setForm({
      ...p,
    });
    setSelectedProduto(p);
    setEditMode(true);
  }

  const [viewMode, setViewMode] = useState(false);
  function viewProduto(p) {
    setSelectedProduto(p);
    setViewMode(true);
  }

  function submit(form) {
    dispatch(Creators.addProduto(form, true, selectedProduto.id));
    setSelectedProduto(undefined);
    setViewMode(false);
    setEditMode(false);
  }

  function confirmDeleteProduto(produto) {
    setSelectedProduto(false);
    dispatch(Creators.deleteProduto(produto.id));
    setDeleteMode(false);
  }

  const _closeFn = useCallback(
    () => setViewMode(false) || setEditMode(false),
    [],
  );

  useEditableContext(editMode);

  return (
    <div className={Styles.Page}>
      <DetailsTable
        open={viewMode}
        produto={selectedProduto}
        renderables={editorRenderables}
        onClose={() => setViewMode(false)}
      />
      <ConfirmationDialog
        open={deleteMode}
        produto={selectedProduto}
        onConfirm={confirmDeleteProduto}
        onClose={() => setDeleteMode(false)}
      />
      {(editMode || viewMode) && (
        <ProductEditor
          close={_closeFn}
          open={editMode}
          submit={(form, _, id) => submit(form, id)}
          renderables={editorRenderables}
          form={form}
          changeEtec
          availableEtecs={T(availableUsers, []).filter((u) =>
            u.roles.find((r) => r.name.toLowerCase().indexOf('etec') !== -1),
          )}
          setter={(e) => {
            const {
              target: { value, name },
            } = e;
            setForm((f) => ({ ...f, [name]: value }));
          }}
        />
      )}
      <Header title='PRODUTOS' icon={faClipboard}>
        <WithPermission permission='produtos.create'>
          <button
            className={C(Styles.Custom, Styles.Success)}
            onClick={createProduto}
          >
            <FontAwesomeIcon icon={faPlus} />
            &nbsp;Criar produto
          </button>
        </WithPermission>
      </Header>
      <main className={Styles.Content}>
        <Table
          produtos={produtos}
          users={availableUsers}
          onEdit={editProduto}
          onRemove={deleteProduto}
          onView={viewProduto}
        />
      </main>
    </div>
  );
}
